import { z } from 'zod';
import { AccountType } from 'further-types/investor';
import * as investorRules from './';
import { Country } from '../../../labels';
import isMongoId from '../../helpers/is-mongo-id';
import {
  CrsClassification,
  FatcaClassification,
} from 'further-types/lp/investor-tax-details';
import { LpInvestorAccessPermissionGroupKey } from 'further-types/further-user';
import { CalendarDay } from '../../../utils';
import {
  ClassificationFlow,
  LpInvestorClassification,
} from 'further-types/lp/investor-classification';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { KycRequestProcess } from 'further-types/lp/kyc';
import { KybRequestProcess } from 'further-types/lp/kyb';

export const SaveIndividualInvestorDetailsInput =
  investorRules.SaveIndividualInvestorDetailsInput.extend({
    investorId: z.string().refine(isMongoId()),
  });

export const SaveLegalEntityDetailsInput =
  investorRules.SaveLegalEntityDetailsInput.extend({
    investorId: z.string().refine(isMongoId()),
  });

export const SaveBankAccountDetailsInput =
  investorRules.SaveBankAccountDetailsInput.extend({
    investorId: z.string().refine(isMongoId()),
  });

export const SaveTaxInformationInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  taxResidencies: z.array(
    z.object({
      country: z.nativeEnum(Country),
      taxIdentificationNumber: z
        .string()
        .min(1, 'Tax identidication number is required.'),
    }),
  ),
  fatcaClassification: z.nativeEnum(FatcaClassification),
  crsClassification: z.nativeEnum(CrsClassification),
});

export const AddNewInvestorApplication = z.discriminatedUnion('accountType', [
  z.object({
    accountType: z.literal(AccountType.Individual),
    firstName: z.string().min(1, { message: 'First name is required.' }),
    middleName: z.string().optional(),
    lastName: z.string().min(1, { message: 'Last name is required.' }),
    addWithCommitment: z.boolean(),
  }),
  z.object({
    accountType: z.literal(AccountType.LegalEntity),
    registeredName: z
      .string()
      .min(1, { message: 'Registered name is required.' }),
    addWithCommitment: z.boolean(),
  }),
]);

export const AddInvestorUserInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  firstName: z.string().min(1, { message: 'First name is required.' }),
  lastName: z.string().min(1, { message: 'Last name is required.' }),
  email: z.string().email({ message: 'Invalid email address.' }),
});

export const UpdateInvestorUserInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  userId: z.string().refine(isMongoId()),
  permissionsGroup: z.nativeEnum(LpInvestorAccessPermissionGroupKey),
  copyIntoSystemEmails: z.boolean(),
  isDefaultUser: z.boolean(),
});

export const RemoveInvestorUserInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  userId: z.string().refine(isMongoId()),
});

export const SaveOnboardingSettingsInput = z.object({
  fundraiseTarget: z.number(),
  targetCloseDate: z.instanceof(CalendarDay).nullable(),
  requestBankDetails: z.boolean(),
  minimumCommitmentSize: z.number().min(0),
  generalCommitmentInstructions: z.string(),
  investorClassificationSettings: z.array(
    z.object({
      classificationFlow: z.nativeEnum(ClassificationFlow),
      availableInvestorClassifications: z.array(
        z.nativeEnum(LpInvestorClassification),
      ),
      requiredTestResult: z.number().nullable(),
    }),
  ),
  defaultInternationalClassificationFlow: z.nativeEnum(ClassificationFlow),
  requireAllSelectedStatuses: z.boolean(),
  requireInvestorExperienceTesting: z.boolean(),
  requireInvestorKnowledgeTesting: z.boolean(),
  reviewSettings: z.object({
    investorDetailsNeedsReview: z.boolean(),
    classificationNeedsReview: z.boolean(),
    taxDetailsNeedsReview: z.boolean(),
    investorDetailsReviewerIds: z.array(z.string().refine(isMongoId())),
    classificationReviewerIds: z.array(z.string().refine(isMongoId())),
    taxDetailsReviewerIds: z.array(z.string().refine(isMongoId())),
  }),
  enableCustomTaxRequests: z.boolean(),
  kycRequestProcess: z.nativeEnum(KycRequestProcess),
  kybRequestProcess: z.nativeEnum(KybRequestProcess),
});

export const SaveCommitmentSettingsInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  commitmentDate: z.instanceof(CalendarDay).optional(),
  externalCommitmentId: z.string().optional(),
  targetCommitment: z.number().optional(),
  investorLocalCurrency: z.nativeEnum(CurrencyCode).optional(),
  lpFeeTemplateId: z
    .string()
    .optional()
    .refine(isMongoId({ optional: true })),
  carriedInterestFeeTemplateId: z
    .string()
    .optional()
    .refine(isMongoId({ optional: true })),
  equalisationInterestFeeTemplateId: z
    .string()
    .optional()
    .refine(isMongoId({ optional: true })),
  documents: z.array(
    z.object({
      path: z.string(),
      name: z.string(),
    }),
  ),
  shouldCompleteCommitment: z.boolean(),
  shouldNotifyInvestor: z.boolean().default(false),
});
